import { useEffect, useCallback } from 'react'

const SIDEBAR_STATE_KEY = 'kt_aside_minimize_state'
const SIDEBAR_FIRST_LOGIN_KEY = 'kt_aside_first_login'

export const useSidebarState = () => {
  const initializeSidebarState = useCallback(() => {
    // Check if this is a fresh login session
    const isFirstLogin = localStorage.getItem(SIDEBAR_FIRST_LOGIN_KEY) === 'true'
    const savedState = localStorage.getItem(SIDEBAR_STATE_KEY)
    
    console.log('Initializing sidebar - isFirstLogin:', isFirstLogin, 'savedState:', savedState)
    
    if (isFirstLogin) {
      // On fresh login, always show sidebar (expanded state)
      console.log('Fresh login - showing expanded sidebar')
      document.body.removeAttribute('data-kt-aside-minimize')
      const toggleButton = document.getElementById('kt_aside_toggle')
      if (toggleButton) {
        toggleButton.classList.remove('active')
      }
      // Clear the first login flag so subsequent page reloads respect user preference
      localStorage.removeItem(SIDEBAR_FIRST_LOGIN_KEY)
      // Set initial state as expanded
      localStorage.setItem(SIDEBAR_STATE_KEY, 'expanded')
    } else {
      // Get saved state from localStorage for returning sessions
      console.log('Regular load - using saved state:', savedState)
      
      if (savedState === 'minimized') {
        console.log('Applying minimized state')
        // Apply minimized state to body
        document.body.setAttribute('data-kt-aside-minimize', 'on')
        
        // Update toggle button state
        const toggleButton = document.getElementById('kt_aside_toggle')
        if (toggleButton) {
          toggleButton.classList.add('active')
        }
      } else {
        console.log('Applying expanded state')
        // Ensure expanded state is applied
        document.body.removeAttribute('data-kt-aside-minimize')
        const toggleButton = document.getElementById('kt_aside_toggle')
        if (toggleButton) {
          toggleButton.classList.remove('active')
        }
      }
    }
  }, [])

  const saveSidebarState = useCallback(() => {
    const body = document.body
    const isMinimized = body.getAttribute('data-kt-aside-minimize') === 'on'
    const state = isMinimized ? 'minimized' : 'expanded'
    
    console.log('Saving sidebar state:', state, 'isMinimized:', isMinimized)
    
    // Save to localStorage
    localStorage.setItem(SIDEBAR_STATE_KEY, state)
  }, [])

  useEffect(() => {
    let handleClick: (() => void) | undefined

    // Delay initialization to ensure Metronic components are ready
    const initTimer = setTimeout(() => {
      initializeSidebarState()

      // Listen for toggle changes using standard DOM event listener
      const toggleButton = document.getElementById('kt_aside_toggle')
      if (toggleButton) {
        handleClick = () => {
          // Wait for DOM to update after click
          setTimeout(saveSidebarState, 100)
        }

        toggleButton.addEventListener('click', handleClick)
      }
    }, 1000) // Wait for Metronic to initialize

    return () => {
      clearTimeout(initTimer)
      // Clean up event listener if it was added
      if (handleClick) {
        const toggleButton = document.getElementById('kt_aside_toggle')
        if (toggleButton) {
          toggleButton.removeEventListener('click', handleClick)
        }
      }
    }
  }, [initializeSidebarState, saveSidebarState])

  const clearSidebarPreferences = useCallback(() => {
    // Remove sidebar state from localStorage
    localStorage.removeItem(SIDEBAR_STATE_KEY)
    // Set flag indicating this is a fresh login
    localStorage.setItem(SIDEBAR_FIRST_LOGIN_KEY, 'true')
  }, [])

  return { initializeSidebarState, saveSidebarState, clearSidebarPreferences }
}

// Utility function to clear sidebar preferences (can be called from anywhere)
export const clearSidebarPreferences = () => {
  localStorage.removeItem(SIDEBAR_STATE_KEY)
  localStorage.setItem(SIDEBAR_FIRST_LOGIN_KEY, 'true')
}